import { useEffect, useMemo, useState } from 'react';
import heartIcon from '../../assets/heart.png';
import heartFillIcon from '../../assets/heart-fill.png';
import { Api } from '../../services/api';
import { saveSelectedProduct } from '../../services/productSelection';
import { normalizeProducts } from '../../services/productNormalizer';
import { resolvePriceInfo, variantBasePrice, formatAmount } from '../../services/pricing';
import { useLanguage } from '../../context/LanguageContext';
import { getStoredCustomerId } from '../../services/customer';
import { toggleProductFavorite } from '../../services/favorites';
import { redirectToLogin } from '../../services/auth';
import {
  getVariantColorKey,
  normalizeColorCode,
} from '../../services/colorUtils';

const PRODUCT_COLOR_FALLBACK = '#d9d9d9';

const getUniqueVariantsByColor = (variants = []) => {
  const seen = new Set();
  const unique = [];
  variants.forEach((variant) => {
    const key = getVariantColorKey(variant);
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(variant);
  });
  return unique;
};

const getLowestPriceVariant = (product) => {
  const variants = product?.variants;
  if (!Array.isArray(variants) || !variants.length) return null;

  return variants.reduce((cheapest, variant) => {
    if (!variant) return cheapest;
    if (!cheapest) return variant;

    const cheapestPrice = variantBasePrice(product, cheapest);
    const candidatePrice = variantBasePrice(product, variant);
    return candidatePrice < cheapestPrice ? variant : cheapest;
  }, null);
};

const ProductsSection = ({
  title,
  variant = 'primary',
  limit = 4,
  seeAllHref = '/products',
  source = 'featured',
  showVariantLowestPrice = false,
}) => {
  const [products, setProducts] = useState([]);
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [variantPreview, setVariantPreview] = useState({
    productId: null,
    variant: null,
  });
  const [favoriteBusyIds, setFavoriteBusyIds] = useState(() => new Set());
  const { t } = useLanguage();
  const customerId = useMemo(getStoredCustomerId, []);

  useEffect(() => {
    let mounted = true;
    const fetcher =
      source === 'new-arrivals' ? Api.fetchNewArrivals : Api.fetchFeaturedProducts;

    fetcher(customerId)
      .then((data) => {
        if (!mounted) return;

        const normalized = normalizeProducts(data);
        setProducts(normalized);
      })
      .catch(() => {
        if (mounted) {
          setProducts([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [customerId, source]);

  const markFavoriteBusy = (productId) => {
    setFavoriteBusyIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const clearFavoriteBusy = (productId) => {
    setFavoriteBusyIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
  };

  const handleToggleFavorite = async (event, product) => {
    event.stopPropagation();
    if (!customerId) {
      redirectToLogin();
      return;
    }
    if (favoriteBusyIds.has(product.id)) return;
    markFavoriteBusy(product.id);
    try {
      const result = await toggleProductFavorite({ product, customerId });
      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id
            ? { ...item, favorite: result.favorite, favoriteId: result.favoriteId }
            : item
        )
      );
    } catch {
      // ignore toggle errors for now
    } finally {
      clearFavoriteBusy(product.id);
    }
  };

  const hasProducts = products.length > 0;
  const visibleProducts = useMemo(() => {
    const resolvedLimit = Number(limit) > 0 ? Number(limit) : 4;
    if (!hasProducts) return [];
    return products.slice(0, resolvedLimit);
  }, [hasProducts, limit, products]);

  return (
    <section className={`products products--${variant}`}>
      <div className="section-heading section-heading--row">
        <div>
          <h2 className="title-section">{title}</h2>
        </div>
        {seeAllHref ? (
          <a href={seeAllHref} className="link">
            {t('products.see_all')}
          </a>
        ) : null}
      </div>
      {hasProducts ? (
        <div className="products__scroller">
          {visibleProducts.map((product) => {
            const isVariantViewActive =
              hoveredProductId === product.id && product.variants?.length;
            const isPreviewingVariant = variantPreview.productId === product.id;
            const previewVariant =
              isPreviewingVariant && variantPreview.variant
                ? variantPreview.variant
                : null;
            const lowestVariant =
              showVariantLowestPrice && !previewVariant
                ? getLowestPriceVariant(product)
                : null;
            const priceInfo = resolvePriceInfo(
              product,
              previewVariant || lowestVariant || null
            );

            const renderPriceRow = () => (
              <div className="product-card__price">
                {priceInfo.showOriginal && (
                  <span className="product-card__old-price">
                    {product.currency}
                    {formatAmount(priceInfo.original)}
                  </span>
                )}
                <span
                  className={`product-card__current-price ${
                    priceInfo.showOriginal
                      ? 'product-card__current-price--discount'
                      : ''
                  }`}
                >
                  {product.currency}
                  {formatAmount(priceInfo.current)}
                </span>
              </div>
            );

            const handleNavigate = () => {
              saveSelectedProduct(product);
              window.location.assign(
                `/product/${encodeURIComponent(product.id || product.productId || '')}`
              );
            };

            const primaryImage = product.images?.[0] || product.image;
            return (
              <article
                key={product.id}
                className={`product-card ${
                  isVariantViewActive ? 'product-card--show-variants' : ''
                }`}
                onMouseEnter={() => setHoveredProductId(product.id)}
                onMouseLeave={() => {
                  setHoveredProductId(null);
                  setVariantPreview({
                    productId: null,
                    variant: null,
                  });
                }}
                onClick={handleNavigate}
              >
                <div className="product-card__media">
                  <img
                    src={previewVariant?.image ? previewVariant.image : primaryImage}
                    alt={product.name}
                    loading="lazy"
                    onError={(event) => {
                    }}
                  />
                  <div className="product-card__actions product-card__actions--overlay">
                    <button
                      type="button"
                      aria-label={t('products.add_to_wishlist')}
                      onClick={(event) => handleToggleFavorite(event, product)}
                      aria-pressed={product.favorite}
                      disabled={favoriteBusyIds.has(product.id)}
                    >
                      <img
                        src={product.favorite ? heartFillIcon : heartIcon}
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
                <div className="product-card__body">
                  {isVariantViewActive ? (
                    <>
                      <div
                        className="product-card__variants"
                        onMouseLeave={() =>
                          setVariantPreview({
                            productId: null,
                            variant: null,
                          })
                        }
                      >
                        {getUniqueVariantsByColor(product.variants || []).map(
                          (variant) => {
                            const normalizedColor = normalizeColorCode(
                              variant.colorCode || variant.color?.code || ''
                            );
                            const swatchColor =
                              normalizedColor || PRODUCT_COLOR_FALLBACK;
                            return (
                              <button
                                key={variant.id}
                                type="button"
                                className="variant-thumb"
                                style={{
                                  backgroundColor: swatchColor,
                                }}
                                onMouseEnter={() =>
                                  setVariantPreview({
                                    productId: product.id,
                                    variant,
                                  })
                                }
                                onClick={(event) => event.stopPropagation()}
                               >
                                 {!normalizedColor && (
                                  <img
                                    src={variant.image}
                                    alt="Variant option"
                                    onError={(event) => {
                                    
                                    }}
                                  />
                                )}
                              </button>
                            );
                          }
                        )}
                      </div>
                      {renderPriceRow()}
                    </>
                  ) : (
                    <>
                      <p className="product-card__brand">{product.brand}</p>
                      <h3>{product.name}</h3>
                      {renderPriceRow()}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="products__empty">{t('products.empty')}</p>
      )}
    </section>
  );
};

export default ProductsSection;
